/**
 * Create an email as a draft in the signed-in user's own Outlook mailbox
 * (PHOTON-117).
 *
 * Tackle My Day used to send customer email through GHL from an
 * `@e.lenslock.com` address, so customer replies landed in GHL where LERTs
 * never saw them. With this, Photon writes the draft into the LERT's Outlook
 * Drafts folder instead; they review and send it from Outlook, and replies
 * come back to their inbox.
 *
 * Uses Microsoft Graph with the user's delegated token — the scope covers
 * only the signed-in user's own mailbox. It needs a one-time admin consent
 * for `Mail.ReadWrite` on the Photon app registration; until that's granted
 * the token request fails and the composer falls back to "Send via CRM".
 * Gated by `enableOutlookDrafts` in config.json.
 */
import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const MAIL_SCOPES = ["https://graph.microsoft.com/Mail.ReadWrite"];
const GRAPH_MESSAGES_URL = "https://graph.microsoft.com/v1.0/me/messages";

export interface OutlookDraftInput {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyPlain: string;
}

export interface OutlookDraftResult {
  id: string;
  webLink: string;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Plain text → simple HTML paragraphs, escaped (the body is user-edited). */
export function plainToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function useOutlookDraft() {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;

  const getMailToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error("Not signed in.");
    try {
      const r = await instance.acquireTokenSilent({ scopes: MAIL_SCOPES, account });
      return r.accessToken;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) throw err;
      // First use (consent) or an expired session. A popup keeps the
      // composer's state; a redirect would throw the draft away.
      const r = await instance.acquireTokenPopup({ scopes: MAIL_SCOPES, account });
      return r.accessToken;
    }
  }, [instance, account]);

  const createDraft = useCallback(
    async (input: OutlookDraftInput): Promise<OutlookDraftResult> => {
      let token: string;
      try {
        token = await getMailToken();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(
          /consent|AADSTS65001|AADSTS90094|admin/i.test(msg)
            ? "Photon doesn't have permission to create Outlook drafts yet (needs admin approval). Use Send via CRM for now."
            : `Couldn't connect to Outlook: ${msg}`,
        );
      }
      const toRecipients = (list: string[]) =>
        list.map((address) => ({ emailAddress: { address } }));
      const resp = await fetch(GRAPH_MESSAGES_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: input.subject,
          body: { contentType: "HTML", content: plainToHtml(input.bodyPlain) },
          toRecipients: toRecipients(input.to),
          ccRecipients: toRecipients(input.cc),
          bccRecipients: toRecipients(input.bcc),
        }),
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const j = await resp.json();
          detail = j?.error?.message || detail;
        } catch {
          /* keep status */
        }
        throw new Error(`Outlook refused the draft: ${detail}`);
      }
      const j = await resp.json();
      return { id: j.id, webLink: j.webLink };
    },
    [getMailToken],
  );

  return { createDraft };
}
